import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  allocateBudget,
  durationChipStatus,
  estimateSeconds,
  formatDurationSeconds,
  getSpeakerNotesForSlide,
  reorderSpeakerNotes,
  resolveSpeakerScriptConfig,
  resolveTargetSeconds,
  setSpeakerScript,
  setSpeakerScriptConfig,
  slideTextHash,
  submitGenerateSpeakerScript,
  totalEstimatedSeconds,
  type SurfDeck,
  type SurfScriptTone,
  type SurfSlide,
} from "@vida/surf-core";
import {
  createSurfApiConfig,
  ensureSurfClientConfig,
  refreshSurfOceanClientConfig,
} from "../../services/surfClientConfig";
import { useSurfJobQueue } from "../../hooks/useSurfJobQueue";
import {
  appendCitationToScript,
  applyReferencesToDeck,
  isSpeakerResearchConfigured,
  removeReferenceFromDeck,
  researchSpeakerReferences,
} from "../../services/speakerResearch";
import { SpeakerScriptEditor, type SpeakerScriptEditorHandle } from "./SpeakerScriptEditor";
import { SpeakerNoteCard } from "./SpeakerNoteCard";
import { SpeakerWhalePanel } from "./SpeakerWhalePanel";
import { SlideCanvas } from "./SlideCanvas";
import {
  getSpeakerWhaleConfigMissing,
  isSpeakerWhaleConfigured,
  speakerWhaleConfigMissingLabel,
} from "../../services/surfOceanConfig";
import { exportSpeakerScriptBookletPdf } from "../../export/surfSpeakerScriptPdfExport";
import { Menu } from "../../ui/Menu";
import { copySpeakerReaderPermalink, speakerReaderPath } from "../../utils/speakerReaderLink";
import { saveReturnPath } from "../../auth";
import { useToast } from "../../ui/useToast";
import { Link, useParams } from "react-router-dom";

const TONES: SurfScriptTone[] = [
  "executive",
  "didactic",
  "narrative",
  "casual",
  "persuasive",
];

type Props = {
  deck: SurfDeck;
  slideId: string;
  slideBackgroundUrl?: string | null;
  layerImageUrls?: Record<string, string>;
  slideBackgroundUrls?: Record<string, string>;
  onSlideIdChange: (slideId: string) => void;
  onDeckChange: (deck: SurfDeck) => void;
  onActivity?: (label: string | null) => void;
};

type SpeakerScriptMode = "generate" | "shorten" | "expand" | "rewrite";

const SCRIPT_ACTION_CONFIRM: Record<SpeakerScriptMode, string> = {
  generate:
    "Generare un nuovo discorso dai contenuti della slide? Il testo attuale verrà sostituito.",
  shorten:
    "Accorciare il discorso corrente al target di durata? Il testo nell'editor verrà sostituito.",
  expand:
    "Allungare il discorso corrente verso il target di durata? Il testo nell'editor verrà sostituito.",
  rewrite:
    "Integrare i riferimenti nel discorso corrente? Il testo nell'editor verrà riscritto.",
};

function slideLabel(slide: SurfSlide, index: number): string {
  const title =
    slide.layers.find((l) => l.type === "text" && l.textRole === "title")?.content?.trim();
  return title ? `${index + 1}. ${title}` : `Slide ${index + 1}`;
}

export function SpeakerPanel({
  deck,
  slideId,
  slideBackgroundUrl,
  layerImageUrls = {},
  slideBackgroundUrls = {},
  onSlideIdChange,
  onDeckChange,
  onActivity,
}: Props) {
  const { deckId } = useParams<{ deckId: string }>();
  const toast = useToast();
  const { enqueueSubmit, hasActiveJobForTarget } = useSurfJobQueue();
  const slideIndex = deck.slides.findIndex((s) => s.id === slideId);
  const slide = slideIndex >= 0 ? deck.slides[slideIndex] : undefined;
  const config = resolveSpeakerScriptConfig(deck);
  const budgetMap = useMemo(() => allocateBudget(deck), [deck]);
  const targetSeconds = slide ? resolveTargetSeconds(deck, slide.id) : 60;

  const [scriptText, setScriptText] = useState(slide?.speakerScript?.text ?? "");
  const [busy, setBusy] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [whaleOpen, setWhaleOpen] = useState(false);
  const [noteToast, setNoteToast] = useState(false);
  const [dragNoteIndex, setDragNoteIndex] = useState<number | null>(null);
  const [dropNoteIndex, setDropNoteIndex] = useState<number | null>(null);
  const [whaleConfigured, setWhaleConfigured] = useState(() => isSpeakerWhaleConfigured());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCommittedRef = useRef(slide?.speakerScript?.text ?? "");
  const editorRef = useRef<SpeakerScriptEditorHandle>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await ensureSurfClientConfig();
      await refreshSurfOceanClientConfig();
      if (!cancelled) setWhaleConfigured(isSpeakerWhaleConfigured());
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const deckText = slide?.speakerScript?.text ?? "";
    setScriptText(deckText);
    lastCommittedRef.current = deckText;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, [slideId]);

  useEffect(() => {
    const deckText = slide?.speakerScript?.text ?? "";
    if (deckText === lastCommittedRef.current) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setScriptText(deckText);
    lastCommittedRef.current = deckText;
  }, [slide?.speakerScript?.text]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const estimated = estimateSeconds(scriptText, config.wordsPerMinute);
  const chipStatus = durationChipStatus(estimated, targetSeconds);
  const totalEst = totalEstimatedSeconds({ ...deck, slides: deck.slides.map((s) =>
    s.id === slideId ? { ...s, speakerScript: { ...s.speakerScript, text: scriptText, status: "edited" as const } } : s,
  ) });
  const totalTarget = (config.totalTargetMinutes ?? 0) * 60;
  const deckBudgetStatus = durationChipStatus(totalEst, totalTarget);

  const isStale =
    slide?.speakerScript?.generatedFromHash &&
    slide?.speakerScript?.text?.trim() &&
    slideTextHash(slide, deck) !== slide.speakerScript.generatedFromHash;

  const commitScript = useCallback(
    (text: string) => {
      if (!slide) return;
      lastCommittedRef.current = text;
      onDeckChange(setSpeakerScript(deck, slide.id, { text, status: "edited" }));
    },
    [deck, onDeckChange, slide],
  );

  const handleScriptChange = (text: string) => {
    setScriptText(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => commitScript(text), 400);
  };

  const readEditorMarkdown = useCallback((): string => {
    const fromEditor = editorRef.current?.getMarkdown();
    if (typeof fromEditor === "string") return fromEditor;
    return scriptText;
  }, [scriptText]);

  const flushEditorScript = useCallback((): string => {
    const text = readEditorMarkdown();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (text !== lastCommittedRef.current) {
      lastCommittedRef.current = text;
      setScriptText(text);
      if (slide) {
        onDeckChange(setSpeakerScript(deck, slide.id, { text, status: "edited" }));
      }
    }
    return text;
  }, [deck, onDeckChange, readEditorMarkdown, slide]);

  const confirmInvasive = (message: string): boolean => window.confirm(message);

  const runGenerate = async (
    mode: SpeakerScriptMode,
    opts?: { fromSlide?: boolean },
  ) => {
    if (!slide) return;
    if (!confirmInvasive(SCRIPT_ACTION_CONFIRM[mode])) return;

    const currentText = flushEditorScript();
    if (mode !== "generate" && !currentText.trim()) {
      onActivity?.("Scrivi un discorso prima di modificare con AI");
      setTimeout(() => onActivity?.(null), 2000);
      return;
    }

    setBusy(true);
    onActivity?.("Generazione discorso…");
    try {
      await ensureSurfClientConfig();
      const cfg = createSurfApiConfig();
      const tone = slide.speakerScript?.tone ?? config.defaultTone;
      const language = slide.speakerScript?.language ?? config.language ?? "it";
      const fromSlide = mode === "generate" || opts?.fromSlide;
      const result = await submitGenerateSpeakerScript(cfg, deck, slide.id, {
        mode,
        tone,
        language,
        targetSeconds,
        existingScript: fromSlide ? undefined : currentText,
      });
      await enqueueSubmit(result.submit);
      onDeckChange(
        setSpeakerScript(result.deck, slide.id, {
          ...slide.speakerScript,
          text: currentText,
          status: "generating",
        }),
      );
    } catch (e) {
      onActivity?.(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => onActivity?.(null), 2000);
    }
  };

  const runExportPdf = async () => {
    setExportingPdf(true);
    onActivity?.("Export PDF discorsi…");
    try {
      await exportSpeakerScriptBookletPdf({
        deck,
        slideBackgroundUrls,
        layerImageUrls,
      });
      onActivity?.("PDF discorsi scaricato");
    } catch (e) {
      onActivity?.(e instanceof Error ? e.message : String(e));
    } finally {
      setExportingPdf(false);
      setTimeout(() => onActivity?.(null), 2500);
    }
  };

  const runBulkGenerate = async () => {
    if (
      !confirmInvasive(
        "Generare i discorsi AI per tutte le slide? I testi esistenti verranno sostituiti.",
      )
    ) {
      return;
    }
    setBusy(true);
    onActivity?.("Generazione discorsi (tutte le slide)…");
    try {
      await ensureSurfClientConfig();
      const cfg = createSurfApiConfig();
      for (let i = 0; i < deck.slides.length; i += 1) {
        const s = deck.slides[i]!;
        onActivity?.(`Discorso slide ${i + 1}/${deck.slides.length}…`);
        const result = await submitGenerateSpeakerScript(cfg, deck, s.id, {
          mode: "generate",
          tone: config.defaultTone,
          language: config.language ?? "it",
        });
        await enqueueSubmit(result.submit, { preparedDeck: result.deck });
      }
    } catch (e) {
      onActivity?.(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => onActivity?.(null), 2000);
    }
  };

  const runResearch = async () => {
    if (!slide) return;
    if (
      !confirmInvasive(
        "Avviare la ricerca riferimenti per questa slide? I risultati precedenti potrebbero essere sostituiti.",
      )
    ) {
      return;
    }
    setBusy(true);
    onActivity?.("Ricerca riferimenti…");
    try {
      const result = await researchSpeakerReferences(deck, slide.id, (p) => {
        onActivity?.(p.label);
      });
      onDeckChange(applyReferencesToDeck(deck, slide.id, result.references));
      onActivity?.(`${result.references.length} riferimenti trovati`);
    } catch (e) {
      onActivity?.(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => onActivity?.(null), 2500);
    }
  };

  const jobBusy = slide ? hasActiveJobForTarget(deck.id, slide.id) : false;
  const refs = slide?.speakerScript?.references ?? [];

  const scriptMenuItems = useMemo(() => {
    const fromSlideItems = scriptText.trim()
      ? [
          {
            id: "regenerate-slide",
            label: "Rigenera da slide",
            disabled: busy || jobBusy,
            onClick: () => void runGenerate("generate", { fromSlide: true }),
          },
        ]
      : [
          {
            id: "generate-slide",
            label: "Genera da slide",
            disabled: busy || jobBusy,
            onClick: () => void runGenerate("generate", { fromSlide: true }),
          },
        ];

    return [
      ...fromSlideItems,
      {
        id: "shorten",
        label: "Accorcia a target",
        disabled: busy || jobBusy || !scriptText.trim(),
        onClick: () => void runGenerate("shorten"),
      },
      {
        id: "expand",
        label: "Allunga a target",
        disabled: busy || jobBusy || !scriptText.trim(),
        onClick: () => void runGenerate("expand"),
      },
      {
        id: "research",
        label: "Ricerca riferimenti",
        disabled: busy || !isSpeakerResearchConfigured(),
        onClick: () => void runResearch(),
      },
      {
        id: "rewrite",
        label: "Integra riferimenti nel discorso",
        disabled: busy || jobBusy || !refs.length || !scriptText.trim(),
        onClick: () => void runGenerate("rewrite"),
      },
    ];
  }, [busy, jobBusy, refs.length, runGenerate, runResearch, scriptText]);

  if (!slide) {
    return <p className="muted">Seleziona una slide.</p>;
  }

  const speakerNotes = getSpeakerNotesForSlide(deck, slide.id);
  const whaleMissingHint = getSpeakerWhaleConfigMissing()
    .map(speakerWhaleConfigMissingLabel)
    .join(", ");
  const atFirstSlide = slideIndex <= 0;
  const atLastSlide = slideIndex >= deck.slides.length - 1;

  const goPrevSlide = () => {
    if (atFirstSlide) return;
    onSlideIdChange(deck.slides[slideIndex - 1]!.id);
  };

  const goNextSlide = () => {
    if (atLastSlide) return;
    onSlideIdChange(deck.slides[slideIndex + 1]!.id);
  };

  return (
    <div className="speaker-panel editor-content-view">
      <header className="speaker-panel-deck-header">
        <div className="speaker-panel-deck-controls">
          <label className="speaker-field">
            <span>Durata totale (min)</span>
            <input
              type="number"
              min={0}
              step={1}
              value={config.totalTargetMinutes ?? ""}
              placeholder="—"
              onChange={(e) => {
                const v = e.target.value === "" ? undefined : Number(e.target.value);
                onDeckChange(setSpeakerScriptConfig(deck, { totalTargetMinutes: v }));
              }}
            />
          </label>
          <label className="speaker-field">
            <span>WPM</span>
            <input
              type="number"
              min={80}
              max={220}
              value={config.wordsPerMinute}
              onChange={(e) => {
                const v = Number(e.target.value) || 140;
                onDeckChange(setSpeakerScriptConfig(deck, { wordsPerMinute: v }));
              }}
            />
          </label>
          <label className="speaker-field">
            <span>Tono default</span>
            <select
              value={config.defaultTone}
              onChange={(e) =>
                onDeckChange(
                  setSpeakerScriptConfig(deck, { defaultTone: e.target.value as SurfScriptTone }),
                )
              }
            >
              {TONES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
        </div>
        {totalTarget > 0 && (
          <div className={`speaker-budget-bar speaker-budget-bar--${deckBudgetStatus}`}>
            Budget deck: {formatDurationSeconds(totalEst)} / {formatDurationSeconds(totalTarget)}
          </div>
        )}
        <button
          type="button"
          className="surf-btn surf-btn--ghost"
          disabled={busy || exportingPdf || !deck.slides.length}
          onClick={() => void runExportPdf()}
        >
          {exportingPdf ? "Export PDF…" : "Export PDF orizzontale"}
        </button>
        <button
          type="button"
          className="surf-btn surf-btn--ghost"
          disabled={busy || !deck.slides.length}
          onClick={() => void runBulkGenerate()}
        >
          Genera discorsi (tutte le slide)
        </button>
        {deckId && (
          <>
            <Link
              to={speakerReaderPath(deckId)}
              className="surf-btn surf-btn--ghost"
              onClick={() => saveReturnPath(speakerReaderPath(deckId))}
            >
              Apri lettore speaker
            </Link>
            <button
              type="button"
              className="surf-btn surf-btn--ghost"
              onClick={() =>
                void copySpeakerReaderPermalink(deckId).then((ok) =>
                  toast.info(ok ? "Permalink copiato" : "Impossibile copiare il link"),
                )
              }
            >
              Copia permalink
            </button>
          </>
        )}
      </header>

      <div className="speaker-panel-slide-picker">
        <div className="speaker-slide-nav">
          <button
            type="button"
            className="surf-btn surf-btn--ghost speaker-slide-nav-btn"
            aria-label="Slide precedente"
            title="Slide precedente"
            disabled={atFirstSlide}
            onClick={goPrevSlide}
          >
            ◀
          </button>
          <label className="speaker-field speaker-slide-select">
            <span>Slide</span>
            <select
              value={slideId}
              onChange={(e) => onSlideIdChange(e.target.value)}
            >
              {deck.slides.map((s, i) => (
                <option key={s.id} value={s.id}>
                  {slideLabel(s, i)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="surf-btn surf-btn--ghost speaker-slide-nav-btn"
            aria-label="Slide successiva"
            title="Slide successiva"
            disabled={atLastSlide}
            onClick={goNextSlide}
          >
            ▶
          </button>
          <span className="speaker-slide-counter" aria-live="polite">
            {slideIndex + 1} / {deck.slides.length}
          </span>
        </div>
      </div>

      {isStale && (
        <div className="speaker-stale-badge" role="status">
          Contenuto slide cambiato — rigenera il discorso
        </div>
      )}

      <div className={`speaker-duration-chip speaker-duration-chip--${chipStatus}`}>
        <span className="speaker-duration-mono">
          stima {formatDurationSeconds(estimated)} / target {formatDurationSeconds(targetSeconds)}
        </span>
      </div>

      <div className="speaker-controls-row">
        <label className="speaker-field">
          <span>Tono</span>
          <select
            value={slide.speakerScript?.tone ?? config.defaultTone}
            onChange={(e) =>
              onDeckChange(
                setSpeakerScript(deck, slide.id, { tone: e.target.value as SurfScriptTone }),
              )
            }
          >
            {TONES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="speaker-field">
          <span>Lingua</span>
          <input
            type="text"
            value={slide.speakerScript?.language ?? config.language ?? "it"}
            onChange={(e) =>
              onDeckChange(setSpeakerScript(deck, slide.id, { language: e.target.value }))
            }
          />
        </label>
        <label className="speaker-field">
          <span>Target (s)</span>
          <input
            type="number"
            min={5}
            value={slide.speakerScript?.targetSeconds ?? budgetMap.get(slide.id) ?? targetSeconds}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v > 0) onDeckChange(setSpeakerScript(deck, slide.id, { targetSeconds: v }));
            }}
          />
        </label>
      </div>

      <div className="speaker-script-layout">
        <aside className="speaker-slide-preview" aria-label="Anteprima slide corrente">
          <span className="speaker-slide-preview-label">{slideLabel(slide, slideIndex)}</span>
          <div className="speaker-slide-preview-canvas">
            <SlideCanvas
              deck={deck}
              slide={slide}
              slideBackgroundUrl={slideBackgroundUrl}
              layerImageUrls={layerImageUrls}
              selectedLayerIds={[]}
              showGuides={false}
              readOnly
              compact
              onSelectLayer={() => undefined}
              onLayerRect={() => undefined}
              onLayerContent={() => undefined}
            />
          </div>
        </aside>
        <div className="speaker-script-editor-col">
          <div className="speaker-script-toolbar">
            <Menu
              align="start"
              disabled={busy || exportingPdf}
              label="⋯"
              items={scriptMenuItems}
            />
          </div>
          <button
            type="button"
            className={`speaker-script-ai-btn${whaleConfigured ? "" : " speaker-script-ai-btn--unconfigured"}`}
            aria-label="Chiedi a Whale"
            title={
              whaleConfigured
                ? "Chiedi approfondimenti all'AI"
                : `Config incompleta: ${whaleMissingHint || "ocean stream / agent id"}. Clicca per i dettagli.`
            }
            onClick={() => {
              void refreshSurfOceanClientConfig().then(() => {
                setWhaleConfigured(isSpeakerWhaleConfigured());
                setWhaleOpen(true);
              });
            }}
          >
            <span className="speaker-script-ai-btn-icon" aria-hidden>
              ✨
            </span>
          </button>
          <SpeakerWhalePanel
            deck={deck}
            slideId={slide.id}
            slideLabel={slideLabel(slide, slideIndex)}
            open={whaleOpen}
            onOpenChange={setWhaleOpen}
            onDeckChange={onDeckChange}
            onActivity={onActivity}
            onNoteAdded={() => {
              setNoteToast(true);
              setTimeout(() => setNoteToast(false), 2000);
            }}
          />
          <SpeakerScriptEditor
            ref={editorRef}
            value={scriptText}
            placeholder="Scrivi o genera il discorso per questa slide…"
            onChange={handleScriptChange}
            onBlur={(text) => commitScript(text)}
          />
        </div>
        <aside className="speaker-notes-rail" aria-label="Note di approfondimento">
          <div className="speaker-notes-rail-head">
            <h3>Approfondimenti</h3>
            {noteToast && <span className="speaker-notes-toast">Nota aggiunta</span>}
          </div>
          {speakerNotes.length === 0 ? (
            <p className="speaker-notes-empty muted">
              Nessuna nota. Usa l&apos;icona ✨ sul discorso per chiedere all&apos;AI e salvare
              approfondimenti riutilizzabili.
            </p>
          ) : (
            <div className="speaker-notes-list">
              {speakerNotes.map((note, index) => (
                <SpeakerNoteCard
                  key={note.id}
                  deck={deck}
                  slideId={slide.id}
                  note={note}
                  index={index}
                  defaultMinimized
                  resetCollapseKey={slide.id}
                  dragOver={dropNoteIndex === index && dragNoteIndex !== null && dragNoteIndex !== index}
                  onDeckChange={onDeckChange}
                  onActivity={onActivity}
                  onDragStart={setDragNoteIndex}
                  onDragOver={setDropNoteIndex}
                  onDrop={(toIdx) => {
                    if (dragNoteIndex === null) return;
                    const raw = slide.speakerScript?.notes ?? [];
                    const fromRaw = raw.findIndex((n) => n.id === speakerNotes[dragNoteIndex]?.id);
                    const toRaw = raw.findIndex((n) => n.id === speakerNotes[toIdx]?.id);
                    if (fromRaw >= 0 && toRaw >= 0) {
                      onDeckChange(reorderSpeakerNotes(deck, slide.id, fromRaw, toRaw));
                    }
                    setDragNoteIndex(null);
                    setDropNoteIndex(null);
                  }}
                />
              ))}
            </div>
          )}
        </aside>
      </div>

      {refs.length > 0 && (
        <section className="speaker-references">
          <h3 className="speaker-references-title">Riferimenti</h3>
          <ul className="speaker-references-list">
            {refs.map((ref) => (
              <li key={ref.id} className="speaker-ref-card">
                <div className="speaker-ref-head">
                  {ref.url ? (
                    <a href={ref.url} target="_blank" rel="noreferrer noopener">
                      {ref.title}
                    </a>
                  ) : (
                    <strong>{ref.title}</strong>
                  )}
                  <span className="muted">{ref.source}</span>
                </div>
                {ref.snippet && <p className="speaker-ref-snippet">{ref.snippet}</p>}
                <div className="speaker-ref-actions">
                  <button
                    type="button"
                    className="surf-btn surf-btn--ghost"
                    onClick={() => onDeckChange(appendCitationToScript(deck, slide.id, ref))}
                  >
                    Inserisci citazione
                  </button>
                  <button
                    type="button"
                    className="surf-btn surf-btn--ghost"
                    onClick={() => onDeckChange(removeReferenceFromDeck(deck, slide.id, ref.id))}
                  >
                    Rimuovi
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
